import { askAboutAndReveal } from "../../../state/chatActions";
import type { MonthlyRow } from "../../../data/types";
import { fmtPct, shortMonth } from "./format";
import styles from "./VampSparkline.module.css";

interface VampSparklineProps {
  monthly: MonthlyRow[];
  /** Above-standard band ≥ this → $8/txn fee. */
  aboveStandard?: number;
  /** Excessive band ≥ this → fee + enhanced monitoring. */
  excessive?: number;
}

export function VampSparkline({
  monthly,
  aboveStandard = 0.5,
  excessive = 0.7,
}: VampSparklineProps) {
  const threshold = excessive;
  const earlyWarning = aboveStandard;
  // Aggregate portfolio VAMP per month.
  const byMonth = new Map<string, { tc: number; purchases: number }>();
  monthly.forEach((r) => {
    if (!r.active) return;
    const existing = byMonth.get(r.month) ?? { tc: 0, purchases: 0 };
    existing.tc += r.tc40 + r.tc15;
    existing.purchases += r.purchases;
    byMonth.set(r.month, existing);
  });

  const months = Array.from(byMonth.keys()).sort();
  const points = months.map((month) => {
    const v = byMonth.get(month)!;
    const vamp = v.purchases > 0 ? (v.tc / v.purchases) * 100 : 0;
    return { month, vamp };
  });

  const width = 560;
  const height = 120;
  const pad = 16;
  const maxY = Math.max(threshold + 0.2, ...points.map((p) => p.vamp));
  const toX = (i: number) =>
    pad + (i / Math.max(1, points.length - 1)) * (width - pad * 2);
  const toY = (v: number) => height - pad - (v / maxY) * (height - pad * 2);

  const path = points
    .map((p, i) => `${i === 0 ? "M" : "L"}${toX(i).toFixed(1)} ${toY(p.vamp).toFixed(1)}`)
    .join(" ");
  const area = `${path} L${toX(points.length - 1).toFixed(1)} ${height - pad} L${toX(0).toFixed(1)} ${height - pad} Z`;

  const latest = points[points.length - 1];
  const peak = points.reduce((a, b) => (b.vamp > a.vamp ? b : a), points[0]);

  return (
    <div className={styles.card}>
      <header className={styles.header}>
        <span className={styles.title}>portfolio vamp</span>
        <span className={styles.muted}>rolling 12mo</span>
      </header>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        className={styles.chart}
        role="img"
        aria-label="portfolio vamp ratio over 12 months"
      >
        <line
          x1={pad}
          x2={width - pad}
          y1={toY(threshold)}
          y2={toY(threshold)}
          className={styles.lineCritical}
        />
        <line
          x1={pad}
          x2={width - pad}
          y1={toY(earlyWarning)}
          y2={toY(earlyWarning)}
          className={styles.lineWarn}
        />
        <path d={area} className={styles.area} />
        <path d={path} className={styles.line} />
        {points.map((p, i) => (
          <circle
            key={p.month}
            cx={toX(i)}
            cy={toY(p.vamp)}
            r={2}
            className={styles.dot}
          />
        ))}
      </svg>
      <footer className={styles.footer}>
        <button
          type="button"
          className={styles.stat}
          onClick={() =>
            askAboutAndReveal(
              `what's driving the current rolling portfolio vamp of ${fmtPct(latest?.vamp ?? 0)}? which merchants contribute most and which are the lever points?`
            )
          }
        >
          <span className={styles.statLabel}>current</span>
          <span className={styles.statValue}>{fmtPct(latest?.vamp ?? 0)}</span>
        </button>
        <button
          type="button"
          className={styles.stat}
          onClick={() =>
            askAboutAndReveal(
              `the portfolio vamp peaked at ${fmtPct(peak?.vamp ?? 0)} in ${shortMonth(peak?.month ?? "")}. what drove that peak and why has it dropped?`
            )
          }
        >
          <span className={styles.statLabel}>peak</span>
          <span className={styles.statValue}>{fmtPct(peak?.vamp ?? 0)}</span>
          <span className={styles.statDim}>{shortMonth(peak?.month ?? "")}</span>
        </button>
        <button
          type="button"
          className={styles.stat}
          onClick={() =>
            askAboutAndReveal(
              `the above-standard band kicks in at ${fmtPct(earlyWarning, 2)} — visa charges $8 per cnp transaction on every txn once we're there. how close are we?`
            )
          }
        >
          <span className={styles.statLabel}>above standard</span>
          <span className={styles.statValue}>{fmtPct(earlyWarning, 2)}</span>
          <span className={styles.statDim}>$8/txn</span>
        </button>
        <button
          type="button"
          className={styles.stat}
          onClick={() =>
            askAboutAndReveal(
              `the excessive band is ${fmtPct(threshold, 2)} — fee plus enhanced monitoring. what happens if we breach, and which merchants are pulling us there?`
            )
          }
        >
          <span className={styles.statLabel}>excessive</span>
          <span className={styles.statValue}>{fmtPct(threshold, 2)}</span>
          <span className={styles.statDim}>fee + monitoring</span>
        </button>
      </footer>
    </div>
  );
}
